import { z } from "zod";

import type { ClientServiceProfileFieldsFragment } from "../../__generated__/graphql";

export const ELDER_SERVICE_ID = "33";

/** Order matters: indices 2-3 are the "requires consultation" tiers. */
export const mobilityStatus = [
  "Independent",
  "Use Walking aid or needs handhold assistance",
  "Uses wheelchair or is Chair-bound",
  "Bedridden needing full assistance",
] as const;

export const mobilityStatusOptions = mobilityStatus.map((m) => ({ value: m, label: m }));

export const symptoms = [
  "Breathlessness or chest pain",
  "Giddiness",
  "Pain",
  "Physical or Verbal Aggression",
] as const;

export const requiredAssistances = [
  "Mobility & Transfers",
  "Bathing & Toileting (including diapering)",
  "Meal Assistance - Feeding, buying of food nearby or preparation of simple meals",
  "Medication Reminders & Serving",
  "Social Companionship & Activities (e.g. Going for walks, coloring)",
  "Light Housekeeping Tasks",
] as const;

export const ageOptions = Array.from({ length: 40 }, (_, i) => ({
  value: `${60 + i} yrs`,
  label: `${60 + i}`,
}));

export const weightOptions = Array.from({ length: 13 }, (_, i) => ({
  value: `${40 + i} kg`,
  label: `${40 + i}`,
}));

export const WEIGHT_OTHERS_OPTION = { value: "others", label: "Others" };

// weight (kg) + mobility combination that requires a consultation before booking
const WEIGHT_CONSULT_THRESHOLD = 50;
const CONSULT_MOBILITY = mobilityStatus.slice(2, 4);

export const elderProfileSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    age: z
      .string()
      .min(1, "Age is required")
      .refine((val) => /^\d.*[a-zA-Z]$/.test(val), {
        message: "Age must be a number and include a unit (e.g., 50 yrs)",
      }),
    weight: z
      .string()
      .min(1, "Weight is required")
      .transform((val) => (val.trim().toLowerCase().endsWith("kg") ? val : `${val} kg`))
      .refine((val) => /^\d.*[a-zA-Z]$/.test(val), {
        message: "Weight must be a number and include a unit (e.g., 50 kg)",
      }),
    mobility_status: z.string().min(1, "Mobility Status is required"),
    symptomps: z.array(z.object({ value: z.string(), selected: z.boolean() })).default([]),
    required_assistances: z
      .array(z.object({ value: z.string(), selected: z.boolean() }))
      .min(1, "Required Assistances is required"),
    additional_notes: z.string().min(1, "Additional Notes is required"),
    primary: z.boolean().default(true),
  })
  .superRefine((val, ctx) => {
    const weightAbove = parseInt(val.weight) > WEIGHT_CONSULT_THRESHOLD;
    const needsConsult = CONSULT_MOBILITY.some((m) => m === val.mobility_status);
    if (weightAbove && needsConsult) {
      ctx.addIssue({
        path: ["mobility_status"],
        code: z.ZodIssueCode.custom,
        message: "Please contact our sales team for more information",
      });
    }
  });

export type ElderProfileFormValues = z.input<typeof elderProfileSchema>;

export const emptyElderValues: ElderProfileFormValues = {
  name: "",
  age: "",
  weight: "",
  mobility_status: "",
  symptomps: symptoms.map((value) => ({ value, selected: false })),
  required_assistances: requiredAssistances.map((value) => ({ value, selected: false })),
  additional_notes: "",
  primary: true,
};

/**
 * Form values -> profileAttributes JSON for the mutation.
 * name/primary are top-level mutation inputs, not attributes.
 * `symptomps` drives the inline care-consultant nudge only and is intentionally not
 * persisted — parity with the legacy form, whose symptom checklist was local state.
 */
export function toElderAttributes(values: ElderProfileFormValues) {
  const {
    name: _name,
    primary: _primary,
    symptomps: _symptomps,
    required_assistances,
    ...rest
  } = values;
  return {
    ...rest,
    symptomps: [] as string[],
    required_assistances: (required_assistances ?? [])
      .filter((r) => r.selected)
      .map((r) => r.value),
  };
}

/** Stored profile -> form values (string[] -> checkbox arrays; name from profile). */
export function fromElderProfile(
  profile: ClientServiceProfileFieldsFragment,
): ElderProfileFormValues {
  const attrs = (profile.profileAttributes ?? {}) as Record<string, unknown>;
  const storedSymptoms = (attrs["symptomps"] as string[] | undefined) ?? [];
  const storedAssistances = (attrs["required_assistances"] as string[] | undefined) ?? [];
  return {
    name: profile.name,
    age: (attrs["age"] as string) ?? "",
    weight: (attrs["weight"] as string) ?? "",
    mobility_status: (attrs["mobility_status"] as string) ?? "",
    symptomps: symptoms.map((value) => ({ value, selected: storedSymptoms.includes(value) })),
    required_assistances: requiredAssistances.map((value) => ({
      value,
      selected: storedAssistances.includes(value),
    })),
    additional_notes: (attrs["additional_notes"] as string) ?? "",
    primary: profile.primary,
  };
}

export const DEFAULT_ELDER_CARE_WHATSAPP_MESSAGE =
  "Hi, I would like to chat about your Elder Care on your page https://app.luce.sg";
